import React from 'react';
import {connect, ConnectedProps} from 'react-redux';
import AbstractLayout from '../AbstractLayout';
import {DefaultLayoutProps, DefaultLayoutState} from '@src/@types/components/layouts/DefaultLayout';
import Header from './components/Header';
import Footer from './components/Footer';
import Sidebar from './components/Sidebar';
import ContentBlock from './components/content/ContentBlock';
import {IReduxStoreState} from '@src/@interfaces';
import {activeThemeDesktopVersion, activeThemeMobileVersion} from '@src/_redux/theme/actions';
import Breadcrumbs from '@src/containers/Breadcrumbs/Breadcrumbs';
import {BreadcrumbItem} from '@src/@types/components/Breadcrumbs';
import {LayoutContext, LayoutContextEvents} from '@src/contexts/LayoutContext';
import {ILayoutContext} from '@src/@interfaces/contexts/ILayoutContext';
import app from '@src/helpers/app';
import {IEvent, ISubscription} from '@src/@interfaces/services/Events';

type P = DefaultLayoutProps;
type S = DefaultLayoutState;

export class DefaultLayout extends AbstractLayout<P & ConnectedProps<typeof connector>, S> {
    state: S = {
        pageTitle: undefined,
        pageTitlesReplacers: undefined,
        breadcrumbs: {
            home: undefined,
            items: undefined,
        },
        loadingIndicatorDisplayed: false,
    };

    footerRef: React.RefObject<HTMLDivElement>;
    headerRef: React.RefObject<HTMLDivElement>;

    layoutContextApi!: ILayoutContext;

    constructor(props: any) {
        super(props);

        this.footerRef = React.createRef<HTMLDivElement>();
        this.headerRef = React.createRef<HTMLDivElement>();

        this.resizeListener = this.resizeListener.bind(this);

        this.updateLayoutContext();
    }

    componentDidMount() {
        addEventListener('resize', this.resizeListener);

        this.resizeListener();
    }

    componentDidUpdate() {
        app('events').dispatch(LayoutContextEvents.UPDATED, this.state);
    }

    componentWillUnmount() {
        removeEventListener('resize', this.resizeListener);
    }

    render() {
        return <LayoutContext.Provider value={this.layoutContextApi}>
            <Header wrapperRef={this.headerRef} displayLoadingIndicator={this.state.loadingIndicatorDisplayed}/>
            <Sidebar/>
            <ContentBlock
                    footerRef={this.footerRef}
                    headerRef={this.headerRef}
            >{this.renderContent()}</ContentBlock>
            <Footer footerRef={this.footerRef}/>
        </LayoutContext.Provider>;
    }

    protected getLayoutContextApi(): ILayoutContext {
        return {
            flushPageTitleAndBreadcrumbs: this.flushPageTitleAndBreadcrumbs.bind(this),
            onUpdate: this.onContextUpdate.bind(this),
            getPageMeta: this.getPageMeta.bind(this),
            setPageMeta: this.setPageMeta.bind(this),
            setPageTitle: this.setPageTitle.bind(this),
            setPageSubTitle: this.setPageSubTitle.bind(this),
            setPageBreadcrumbHome: this.setPageBreadcrumbHome.bind(this),
            setPageBreadcrumbItems: this.setPageBreadcrumbItems.bind(this),
            pushPageBreadcrumbItems: this.pushPageBreadcrumbItems.bind(this),
            isLoadingIndicatorDisplayed: this.isLoadingIndicatorDisplayed.bind(this),
            showLoadingIndicator: this.showLoadingIndicator.bind(this),
            hideLoadingIndicator: this.hideLoadingIndicator.bind(this),
        };
    }

    protected updateLayoutContext(): ILayoutContext {
        return this.layoutContextApi = this.getLayoutContextApi();
    }

    protected flushPageTitleAndBreadcrumbs(): void {
        this.setState({
            pageTitle: undefined,
            breadcrumbs: {
                home: undefined,
                items: undefined,
            },
        });
    }

    protected onContextUpdate(listener: (event: IEvent, state: Partial<DefaultLayoutState>) => void): ISubscription {
        return app('events').listen(LayoutContextEvents.UPDATED, listener);
    }

    protected isLoadingIndicatorDisplayed(): boolean {
        return this.state.loadingIndicatorDisplayed;
    }

    protected getPageMeta() {
        return this.state;
    }

    protected setPageMeta(metadata: Pick<S, 'pageTitle' | 'pageSubTitle' | 'pageTitlesReplacers'> & {
        breadcrumbsItems?: BreadcrumbItem[],
        breadcrumbsHome?: BreadcrumbItem
    }) {
        const {breadcrumbsItems, breadcrumbsHome, ...rest} = metadata;
        const breadcrumbs: S['breadcrumbs'] = {};

        if ('breadcrumbsItems' in metadata) {
            breadcrumbs.items = breadcrumbsItems;
        }

        if ('breadcrumbsHome' in metadata) {
            breadcrumbs.home = breadcrumbsHome;
        }

        this.setState(state => ({
            ...state,
            ...rest,
            breadcrumbs: {
                ...state.breadcrumbs,
                ...breadcrumbs,
            }
        }));
    }

    protected setPageTitle(title?: string): void {
        this.setState({pageTitle: title});
    }

    protected setPageSubTitle(title?: string): void {
        this.setState({pageSubTitle: title});
    }

    protected setPageBreadcrumbHome(homeItem: BreadcrumbItem): void {
        this.setState({
            breadcrumbs: {
                home: homeItem,
                ...this.state.breadcrumbs,
            }
        });
    }

    protected setPageBreadcrumbItems(items: BreadcrumbItem[]): void {
        this.setState({
            breadcrumbs: {
                ...this.state.breadcrumbs,
                items,
            }
        });
    }

    protected pushPageBreadcrumbItems(...items: BreadcrumbItem[]): void {
        this.setState({
            breadcrumbs: {
                ...this.state.breadcrumbs,
                items: [
                    ...(this.state.breadcrumbs.items ?? []),
                    ...items
                ]
            }
        });
    }

    protected showLoadingIndicator(): void {
        if (this.state.loadingIndicatorDisplayed) {
            return;
        }

        this.setState({loadingIndicatorDisplayed: true});
    }

    protected hideLoadingIndicator(): void {
        if (!this.state.loadingIndicatorDisplayed) {
            return;
        }

        this.setState({loadingIndicatorDisplayed: false});
    }

    protected renderBreadcrumbs() {
        if (!this.state.pageTitle) {
            return super.renderBreadcrumbs();
        }

        return <Breadcrumbs
                pageTitle={this.state.pageTitle}
                pageSubTitle={this.state.pageSubTitle}
                pageTitlesReplacers={this.state.pageTitlesReplacers}
                home={this.state.breadcrumbs.home}
                items={this.state.breadcrumbs.items ?? []}
        />;
    }

    protected resizeListener() {
        if (!window) {
            return;
        }

        const windowWidth = window.innerWidth;
        const isMobileVersion = windowWidth <= this.props.mobileVersionMaxWidth;
        if (isMobileVersion === this.props.isMobileVersion) {
            return;
        }

        if (isMobileVersion) {
            this.props.activeThemeMobileVersion();
        } else {
            this.props.activeThemeDesktopVersion();
        }
    }
}

export const connector = connect((state: IReduxStoreState) => ({
    isMobileVersion: state.theme.isMobileVersion,
    mobileVersionMaxWidth: state.theme.mobileVersionMaxWidth,
}), {
    activeThemeMobileVersion,
    activeThemeDesktopVersion,
});

export default connector(DefaultLayout);
